import * as DateFns from "date-fns";
import S from "s-js";
import { range } from "src-lib/util/arrays";
import { delegateEvent } from "src-lib/util/dom";
import { idiv } from "src-lib/util/math";
import { assertNever } from "src-lib/util/util";
import * as Surplus from "surplus";
import { InputCheckbox, InputCheckboxIndeterminate } from "surplus-forms";
import { DottedUnderline } from "../../util/decorators";
import { Tooltip } from "../Tooltip";
import {
    DisplayType,
    displayTypeOptions,
    opt2Year,
    partitionByYear,
    SheepChartState,
    SheepPrice,
    valueDisplay,
    WeightCategory,
    weightFilterLabels,
} from "./SheepChart";

const PARTITION_SIZE = document.body.clientWidth > 1200 ? 20 : 30;

export function DataTables(props: { state: SheepChartState }) {
    const { state } = props;
    const data = state.data.prices();
    if (data === undefined) return null;

    return state.form.viewYearly().value
        ? YearlyTable(data)
        : RangedTable(props.state.computed.rangedPrices());

    function YearlyTable(data: SheepPrice[][]): JSX.Element {
        const displayType = state.form.displayType().value,
            weight = state.form.selectedWeightCategory().value,
            displayTypeOption = displayTypeOptions.find(
                (it) => it.id === displayType.id,
            ) as typeof displayTypeOptions[0],
            selectedCategory = state.form.selectedWeightCategory().value,
            maxWeekIndex = DateFns.getISOWeek(new Date()),
            maxYear = DateFns.getYear(new Date()),
            startWeek = state.form.startWeek().value,
            endWeek = state.form.endWeek().value;

        const selectedYears = state.yearCategories(),
            dataByYear = partitionByYear(data, weight.id, selectedYears, {
                start: startWeek,
                end: endWeek,
            }),
            maxEntries = dataByYear.reduce((max, it) => Math.max(max, it.length), 0),
            partitions = range(0, idiv(maxEntries, PARTITION_SIZE) + 1);

        return partitions.map((partitionIndex) => {
            const rowEvents = {
                onMouseEnter: S.data(undefined as HTMLElement | undefined),
                onMouseLeave: S.data(undefined as HTMLElement | undefined),
            };
            const rows = range(
                partitionIndex * PARTITION_SIZE,
                Math.min(maxEntries, (1 + partitionIndex) * PARTITION_SIZE),
            );
            const rowHover = S.data(undefined as any as typeof dataByYear[0]);
            const tbody = (
                <tbody>
                    {rows.map((index) => {
                        let largestValue = Number.MIN_SAFE_INTEGER;
                        for (let year = 0; year < selectedYears.length; year++) {
                            const datum = dataByYear[year][index].value;
                            largestValue =
                                datum === undefined
                                    ? largestValue
                                    : Math.max(largestValue, valueDisplay(datum, displayType.id) || 0);
                        }
                        if (largestValue === 0) largestValue = Number.MIN_SAFE_INTEGER; // Not necessary to highlight lowest number when it's 0
                        const domRow = (
                            <tr>
                                <td>{dataByYear[0][index].week}</td>
                                {selectedYears.map((it, yearIndex) => {
                                    const datum = dataByYear[yearIndex][index].value;
                                    const v =
                                        datum !== undefined ? valueDisplay(datum, displayType.id) : undefined;
                                    if (!datum) console.log(maxWeekIndex, maxYear);
                                    return (
                                        <td class={v === largestValue ? "col-largest" : ""}>
                                            {datum
                                                ? formatDisplay(v!, displayType.id)
                                                : opt2Year[it] >= maxYear && index >= maxWeekIndex
                                                ? ""
                                                : "N/A"}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                        return domRow;
                    })}
                </tbody>
            );

            window.requestAnimationFrame(() => {
                delegateEvent(
                    tbody,
                    "TR",
                    {
                        mouseenter: (e, uid) => {
                            rowEvents.onMouseEnter(e);
                            rowHover(selectedYears.map((it, i) => dataByYear[i][uid!]));
                        },
                        mouseleave: (e, uid) => {
                            rowEvents.onMouseLeave(e);
                        },
                    },
                    rows,
                );
            });
            return (
                <div>
                    <Tooltip
                        minWidth={selectedYears.length * 100} // approx. width, 75 pixels for each column
                        margin={{ left: 65 }}
                        label={
                            <div class="rowTooltipContent">
                                {rowHover()?.map((it) => {
                                    return (
                                        <div>
                                            {it.value ? DateFns.format(it.value.date, "MMM-dd") : "N/A"}
                                        </div>
                                    );
                                })}
                            </div>
                        }
                        event={rowEvents}
                    />
                    <table class="tableSheep">
                        <thead>
                            <tr>
                                <th colSpan={selectedYears.length + 1}>
                                    {displayTypeOption.tooltipText ? (
                                        <Tooltip label={displayTypeOption.tooltipText}>
                                            <DottedUnderline>
                                                Weight ({weightFilterLabels[selectedCategory.id]}),{" "}
                                                {displayTypeOption.value}
                                            </DottedUnderline>
                                        </Tooltip>
                                    ) : (
                                        <span>
                                            Weight ({weightFilterLabels[selectedCategory.id]}),{" "}
                                            {displayTypeOption.value}
                                        </span>
                                    )}
                                </th>
                            </tr>
                            <tr>
                                <th>Week</th>
                                {selectedYears.map((it) => (
                                    <th>{opt2Year[it].toString()}</th>
                                ))}
                            </tr>
                        </thead>
                        {tbody}
                    </table>
                </div>
            );
        }) as any;
    }

    function RangedTable(data: SheepPrice[][]): JSX.Element {
        const selectedWeightCategories = state.weightCategories(),
            displayType = state.form.displayType().value,
            partitions = range(0, idiv(data.length, PARTITION_SIZE) + 1);

        return partitions.map((partitionIndex) => {
            return (
                <div>
                    <table class="tableSheep">
                        <thead>
                            <tr>
                                <th colSpan={selectedWeightCategories.length + 1}>
                                    {displayType.tooltipText ? (
                                        <Tooltip label={displayType.tooltipText}>
                                            <DottedUnderline> Metric ({displayType.value})</DottedUnderline>
                                        </Tooltip>
                                    ) : (
                                        <span>Metric ({displayType.value})</span>
                                    )}
                                </th>
                            </tr>
                            <tr>
                                <th>Date</th>
                                {selectedWeightCategories.map((it) => (
                                    <th>
                                        <Tooltip
                                            label={it !== WeightCategory.SheepAllWeights ? "Lamb" : "Sheep"}>
                                            <DottedUnderline>{weightFilterLabels[it]}</DottedUnderline>
                                        </Tooltip>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {range(
                                partitionIndex * PARTITION_SIZE,
                                Math.min(data.length, (1 + partitionIndex) * PARTITION_SIZE),
                            ).map((i) => {
                                let largestValue = Number.MIN_SAFE_INTEGER;
                                for (let index = 0; index < selectedWeightCategories.length; index++) {
                                    largestValue = Math.max(
                                        largestValue,
                                        valueDisplay(
                                            data[i][selectedWeightCategories[index] - 1],
                                            displayType.id,
                                        ) || 0,
                                    );
                                }
                                const domRow = (
                                    <tr>
                                        <td>{DateFns.format(data[i][0].date, "yyyy-MM-dd")}</td>
                                        {selectedWeightCategories.map((it) => {
                                            const v = valueDisplay(data[i][it - 1], displayType.id);
                                            return (
                                                <td class={v === largestValue ? "col-largest" : ""}>
                                                    {formatDisplay(v, displayType.id)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                                return domRow;
                            })}
                        </tbody>
                    </table>
                </div>
            );
        }) as any;
    }
}

export function ImportantEventsTable(props: { state: SheepChartState }) {
    const { state } = props;
    const rangedHolidays = state.computed.rangedHolidays(),
        enabledHolidays = S.sample(state.form.enabledHolidays);
    // hack. sort of. rangedHolidays and enabledHolidays dynamically update but in different update cycles.
    // They are supposed to be the same length, but for 1 update cycle this isn't the case.
    if (rangedHolidays.length !== enabledHolidays.value().length) return null;

    const selectAll = S.data(S.sample(enabledHolidays.value).every((it) => S.sample(it).value));
    let refInput: HTMLInputElement = undefined as any,
        refSpan: HTMLInputElement = undefined as any;

    return (
        <div>
            <h2>Holidays</h2>
            {enabledHolidays.value().length === 0 ? (
                "No data"
            ) : (
                <table class="holidays">
                    <thead>
                        <tr class="cur-ptr" onClick={handleOnSelectAll}>
                            <th>
                                <label
                                    class="label-inline"
                                    for="select-all"
                                    onMouseDown={(e) => e.preventDefault()}>
                                    <InputCheckboxIndeterminate
                                        checkboxSignals={state.form.enabledHolidays}
                                        selectAll={selectAll}
                                    />
                                    <span ref={refSpan} />
                                </label>
                            </th>
                            <th>Date</th>
                            <th>Event</th>
                        </tr>
                    </thead>
                    <tbody>
                        {enabledHolidays.value.map((it, _, i) => {
                            return (
                                <tr class="cur-ptr" onClick={toggleRow}>
                                    <td onClick={(e) => e.preventDefault()}>
                                        <label class="label-inline" for={it().name}>
                                            <InputCheckbox data={it} />
                                            <span />
                                        </label>
                                    </td>
                                    <td>{DateFns.format(rangedHolidays[i].date, "yyyy-MM-dd")}</td>
                                    <td>{rangedHolidays[i].label}</td>
                                </tr>
                            );

                            function toggleRow(e: MouseEvent) {
                                e.stopPropagation();
                                const curr = S.sample(it);
                                it({ ...curr, value: !curr.value, __v: curr.__v + 1 });
                            }
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );

    function handleOnSelectAll(e: MouseEvent) {
        e.stopPropagation();
        if (e.target === refInput || e.target === refSpan) return;

        const enabled = S.sample(enabledHolidays.value);
        selectAll(!enabled.every((it) => S.sample(it).value));
    }
}

function formatDisplay(data: number | null, id: DisplayType): string {
    if (data === null) return "-";
    switch (id) {
        case DisplayType.CWT:
            return `$${data.toFixed(0)}`;
        case DisplayType.Gross:
            return `$${data.toFixed(2)}`;
        case DisplayType.HeadCount:
            return data.toString();
        default:
            return assertNever(id);
    }
}
